<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import MCPServerApiHandler from '@/api/mcp-server'
import { useCompanySettings } from '@/utils/company-settings'
import { HEADER_SIZES } from '@/utils/components-configurations/page-content-header'
import { COMPANY_SETTINGS_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { showNotify } from '@/utils/notify'

import AppHrefLink from '@/components/AppHrefLink'
import FormFieldNext from '@/components/form/FormFieldNext'
import AppBackButton from '@/components/ui/AppBackButton'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppInput from '@/components/ui/AppInput/AppInput'
import AppTitle from '@/components/ui/AppTitle/AppTitle'
import AppToggle from '@/components/ui/AppToggle/AppToggle'
import PageContentHeader from '@/components/ui/PageContentHeader/PageContentHeader'

defineOptions({
  name: 'MCPSettings'
})

const { t } = useI18n()

const store = useStore()

const savedValue = computed(() => {
  return store.state.system.settings[COMPANY_SETTINGS_ENTITY_KEYS.MCP_ENABLED] || false
})

const { saveSettingsParameter } = useCompanySettings()

const onToggleMCPEnabled = async value => {
  await saveSettingsParameter({
    parameter: COMPANY_SETTINGS_ENTITY_KEYS.MCP_ENABLED,
    value
  })
  if (!value) {
    await onRevokeTokens(false)
  }
}

const isRevokeLoading = ref(false)

const api = new MCPServerApiHandler()

const onRevokeTokens = async (isManual = true) => {
  isRevokeLoading.value = isManual
  try {
    await api.revokeTokens()
    if (isManual) {
      showNotify()
    }
  } catch (error) {
    handleError({ error })
  } finally {
    isRevokeLoading.value = false
  }
}
</script>

<template>
  <div class="mcps-Page">
    <AppBackButton />

    <PageContentHeader :level="3" :max-width="HEADER_SIZES.LG" no-margin title="MCP" />

    <div class="mcps-Page_Content">
      <AppToggle
        :model-value="savedValue"
        :disabled="isRevokeLoading"
        :label="t('mcp.toggle.label')"
        reverse
        @update:model-value="onToggleMCPEnabled"
      />

      <FormFieldNext :label="t('mcp.server_url.label')" class="mcps-FormField">
        <AppInput
          model-value="https://mcp.oboard.io/mcp"
          copyable
          readonly
          size="xlg"
          style-type="primary"
        />
      </FormFieldNext>

      <div class="mcps-ContentSection">
        <div class="mcps-ContentSection_Header">
          <AppTitle :level="4" disable-margin>
            {{ t('mcp.revoke_tokens.title') }}
          </AppTitle>
          <div class="mcps-Description">
            {{ t('mcp.revoke_tokens.description') }}
          </div>
        </div>
        <AppButtonNext
          color="--grade-low-color-next"
          icon-before="close-common"
          :loading="isRevokeLoading"
          @click="onRevokeTokens(true)"
        >
          {{ t('action.revoke') }}
        </AppButtonNext>
      </div>

      <div>
        <AppTitle :level="4" disable-top-margin> {{ t('connections.help.title') }}</AppTitle>
        <AppHrefLink
          href="https://oboard.atlassian.net/wiki/spaces/OBPFJ/pages/2207449090"
          prepend-icon="guide"
        >
          {{ t('connections.guide.link_text') }}
        </AppHrefLink>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '~@/assets/styles/integrations-steps';

$content-width: 400px;

.mcps-Page_Content {
  width: 100%;
  max-width: 100%;
  padding-left: $page-left-padding;
  padding-right: $page-right-padding;
  display: flex;
  flex-direction: column;
  gap: 28px;
  align-items: flex-start;
}
.mcps-FormField {
  width: $content-width;
}

.mcps-ContentSection {
  width: $content-width;
  @extend %step-styles;
}

.mcps-ContentSection_Header {
  @extend %step-header-styles;
}

.mcps-Description {
  @extend %step-description-styles;
}
</style>
